import type { TeachingDocumentModel } from "@/api/cle-api";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import DocumentIcon from "@/components/icons/DocumentIcon";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";

import { DocumentPaginationControls } from "./DocumentPaginationControls";
import { StatusBadge } from "./StatusBadge";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "./utils/evidenceStatus";
import { formatBoolean, formatDate } from "./utils/formatters";
import type { MockPagination } from "./types";

interface TeachingsTableProps {
  teachings: TeachingDocumentModel[];
  isLoading: boolean;
  error: Error | null;
  onSelectTeaching: (teachingId: string) => void;
  pagination: MockPagination;
  showPagination?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

export const TeachingsTable = ({
  teachings,
  isLoading,
  error,
  onSelectTeaching,
  pagination,
  showPagination = true,
  onPreviousPage,
  onNextPage,
}: TeachingsTableProps) => (
  <>
    <TableRoot>
      <TableHead>
        <th>Dokument</th>
        <th>Lektioner</th>
        <th>Uploadet den</th>
        <th>Rapporteret</th>
        <th>Status</th>
        <th>Handling</th>
      </TableHead>
      <TableBody>
        {isLoading && (
          <tr>
            <td colSpan={6} className="text-muted">
              Henter undervisning ...
            </td>
          </tr>
        )}
        {error && (
          <tr>
            <td colSpan={6}>
              <CleApiErrorText
                error={error}
                fallback="Kunne ikke hente undervisning."
              />
            </td>
          </tr>
        )}
        {!isLoading &&
          !error &&
          teachings.map((teaching) => (
            <tr
              key={teaching.id}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onSelectTeaching(teaching.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectTeaching(teaching.id);
                }
              }}
            >
              <td>
                <div className="d-flex align-items-center gap-2">
                  <DocumentIcon height={16} />
                  {teaching.fileName ?? "-"}
                </div>
              </td>
              <td>{teaching.numberOfLessons}</td>
              <td>{formatDate(teaching.uploadedAt)}</td>
              <td>{formatBoolean(teaching.reported)}</td>
              <td>
                {teaching.status ? (
                  <StatusBadge
                    status={formatEvidenceStatus(teaching.status)}
                    variant={getEvidenceStatusVariant(teaching.status)}
                  />
                ) : (
                  "-"
                )}
              </td>
              <td>
                <button
                  id={`efteruddannelse-teaching-${teaching.id}`}
                  type="button"
                  className="btn btn--action"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectTeaching(teaching.id);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          ))}
        {!isLoading && !error && !teachings.length && (
          <tr>
            <td colSpan={6} className="text-muted">
              Ingen undervisningsdokumenter
            </td>
          </tr>
        )}
      </TableBody>
    </TableRoot>
    <div className="d-flex justify-content-end mt-2">
      <DocumentPaginationControls
        pagination={pagination}
        showPagination={showPagination}
        onPreviousClick={onPreviousPage}
        onNextClick={onNextPage}
      />
    </div>
  </>
);
